import {
  Button,
  Loader,
  PageHeader,
  Panel,
  PanelContent,
  Select,
  TextField
} from '@deque/cauldron-react';
import { type TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AICreditsTargetType } from '~/common/api-client';
import PageTitle from '~/common/components/PageTitle';
import { useAuthContext } from '~/common/contexts/auth';
import { useGlobalToast } from '~/common/contexts/globalToast';
import useAdminAICredits from '../../hooks/useAdminAICredits';
import styles from './AICreditsAdmin.css';

const ADMIN_GROUP = window.appConfig.FEATURE_FLAG_ADMIN_GROUP;
const MAX_USED_CREDITS = 2147483647;
const LOAD_STATUS_ID = 'load-status';
const SAVE_STATUS_ID = 'save-status';

if (!ADMIN_GROUP) {
  throw new Error('Missing FEATURE_FLAG_ADMIN_GROUP variable');
}

const reportFailure = (t: TFunction, requestError: unknown): string =>
  t('Unable to load or update AI credits: {{error}}', {
    error: (requestError as Error).message
  });

const AICreditsAdmin: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { setContents } = useGlobalToast();
  const [targetType, setTargetType] = React.useState<AICreditsTargetType | ''>(
    ''
  );
  const [targetId, setTargetId] = React.useState('');
  const [usedCredits, setUsedCredits] = React.useState('');
  const { data, loading, load, save } = useAdminAICredits(user?.token ?? '');

  const targetTypeRef = React.useRef<HTMLSelectElement>(null);
  const targetIdFieldRef = React.useRef<HTMLInputElement>(null);
  const usedCreditsFieldRef = React.useRef<HTMLInputElement>(null);
  const loadButtonRef = React.useRef<HTMLButtonElement>(null);

  const isAdmin = user?.groups.includes(ADMIN_GROUP) ?? false;
  const trimmedTargetId = targetId.trim();
  const parsedUsedCredits = Number(usedCredits);
  const hasValidUsedCredits =
    usedCredits.trim() !== '' &&
    Number.isInteger(parsedUsedCredits) &&
    parsedUsedCredits >= 0 &&
    parsedUsedCredits <= MAX_USED_CREDITS;
  const targetBlockedReason =
    targetType === ''
      ? t('Select a target type.')
      : trimmedTargetId === ''
        ? t('Enter a target ID.')
        : null;

  // Spelled out rather than derived from targetBlockedReason so TypeScript can
  // narrow targetType away from '' at the load/save call sites.
  const canLoad = !loading && targetType !== '' && trimmedTargetId !== '';
  const currentData =
    data?.target_type === targetType && data.target_id === trimmedTargetId
      ? data
      : null;
  const usedCreditsError =
    usedCredits.trim() !== '' && !hasValidUsedCredits
      ? t('Enter a whole number between 0 and {{max}}.', {
          max: MAX_USED_CREDITS
        })
      : null;

  const loadBlockedReason = loading ? null : targetBlockedReason;

  const saveBlockedReason = loading
    ? null
    : (targetBlockedReason ??
      (currentData === null
        ? t('Load a target before saving.')
        : !hasValidUsedCredits
          ? t('Enter a valid used-credits value.')
          : null));

  const canSave = canLoad && saveBlockedReason === null;

  if (user === undefined) {
    return (
      <div className={styles.page}>
        <PageTitle title={t('AI Credits Admin Panel')} />
        <PageHeader heading={<h1>{t('AI Credits Admin Panel')}</h1>} />
        <Loader label={t('Checking permissions')} />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <PageTitle title={t('AI Credits Admin Panel')} />
        <PageHeader heading={<h1>{t('AI Credits Admin Panel')}</h1>} />
        <p role="alert">{t('You do not have permission to see this page.')}</p>
      </div>
    );
  }

  const focusBlockedTarget = () => {
    if (targetType === '') {
      targetTypeRef.current?.focus();
    } else {
      targetIdFieldRef.current?.focus();
    }
  };

  const handleLoad = async () => {
    if (!canLoad) {
      if (loadBlockedReason) {
        setContents(loadBlockedReason, 'error');
        focusBlockedTarget();
      }
      return;
    }
    try {
      await load(targetType, trimmedTargetId);
      setContents(t('AI credits loaded successfully'), 'confirmation');
    } catch (requestError) {
      setContents(reportFailure(t, requestError), 'error');
    }
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) {
      if (saveBlockedReason) {
        setContents(saveBlockedReason, 'error');
        if (targetBlockedReason) {
          focusBlockedTarget();
        } else if (currentData === null) {
          loadButtonRef.current?.focus();
        } else {
          usedCreditsFieldRef.current?.focus();
        }
      }
      return;
    }
    try {
      await save(targetType, trimmedTargetId, parsedUsedCredits);
      setContents(t('AI credits saved successfully'), 'confirmation');
    } catch (requestError) {
      setContents(reportFailure(t, requestError), 'error');
    }
  };

  const idLabel =
    targetType === 'enterprise'
      ? t('Enterprise ID (Billing UUID)')
      : t('User ID (Keycloak ID)');

  return (
    <div className={styles.page}>
      <PageTitle title={t('AI Credits Admin Panel')} />
      <PageHeader
        className={styles.pageHeader}
        heading={<h1>{t('AI Credits Admin Panel')}</h1>}
      />
      <Panel>
        <PanelContent>
          {loading && <Loader label={t('Loading AI credits')} />}
          <form className={styles.form} onSubmit={handleSave}>
            <Select
              ref={targetTypeRef}
              label={t('Target type')}
              value={targetType}
              options={[
                { key: '', value: '', label: t('Select target type') },
                { key: 'user', value: 'user', label: t('User') },
                {
                  key: 'enterprise',
                  value: 'enterprise',
                  label: t('Enterprise')
                }
              ]}
              onChange={event =>
                setTargetType(event.target.value as AICreditsTargetType | '')
              }
              required
            />
            <TextField
              fieldRef={targetIdFieldRef}
              label={idLabel}
              value={targetId}
              onChange={setTargetId}
              required
            />
            <TextField
              fieldRef={usedCreditsFieldRef}
              label={t('Used credits')}
              error={usedCreditsError}
              type="number"
              min={0}
              max={MAX_USED_CREDITS}
              step={1}
              value={usedCredits}
              onChange={setUsedCredits}
              required
            />
            <dl className={styles.values}>
              <div className={styles.valueRow}>
                <dt>{t('Current used credits')}</dt>
                <dd>{currentData ? currentData.used : t('Not loaded')}</dd>
              </div>
              <div className={styles.valueRow}>
                <dt>{t('Current credit limit')}</dt>
                <dd>{currentData ? currentData.limit : t('Not loaded')}</dd>
              </div>
            </dl>
            <p
              id={LOAD_STATUS_ID}
              className={styles.precondition}
              data-testid={LOAD_STATUS_ID}
            >
              {loadBlockedReason ?? ''}
            </p>
            <p
              id={SAVE_STATUS_ID}
              className={styles.precondition}
              data-testid={SAVE_STATUS_ID}
            >
              {saveBlockedReason ?? ''}
            </p>
            {/* aria-disabled (not disabled) keeps these reachable by keyboard
                and screen reader; handleLoad/handleSave hold the real guard. */}
            <div className={styles.actions}>
              <Button
                ref={loadButtonRef}
                type="button"
                variant="secondary"
                onClick={handleLoad}
                aria-disabled={!canLoad}
                aria-describedby={
                  loadBlockedReason ? LOAD_STATUS_ID : undefined
                }
              >
                {t('Load')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                aria-disabled={!canSave}
                aria-describedby={
                  saveBlockedReason ? SAVE_STATUS_ID : undefined
                }
              >
                {t('Save')}
              </Button>
            </div>
          </form>
        </PanelContent>
      </Panel>
    </div>
  );
};

export default AICreditsAdmin;
